import './FeedNotification.sass';

import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { FeedNotificationProps } from './FeedNotification.interface';
import { Button } from '$uikit';
import router from '$navigation/router';
import { BIND_EMAIL } from '$shared/constants/pages';
import { feedActions } from '$store/feed';
import { useTranslator } from '$hooks';
import { classNames, getStorageItem, setStorageItem, statReachGoal } from '$utils';
import { mainActions } from '$store/main';
import { initPush } from '$services';
import { balancePayActions } from '$store/balancePay';
import { BalancePayAction } from '$store/balancePay/interface';
import { SubscriptionType } from '$store/models';

export const FeedNotification: FC<FeedNotificationProps> = ({
  notification,
  skipClose = false,
}) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { style = 'default', action, actionParams } = notification;

  useEffect(() => {
    if (notification && notification.id === 'online_models') {
      statReachGoal('feed_go_to_online_models_show');
    }
  }, [notification]);

  const handleClick = useCallback(
    ({ target }) => {
      if (target.closest('.Button.quinary')) {
        if (notification.id === 'install_prompt') {
          dispatch(mainActions.setShowInstallPrompt(false));
        } else {
          dispatch(feedActions.hideNotification(notification.id));
        }
      } else {
        statReachGoal('feed_notify_press');
        if (notification.id === 'install_prompt') {
          // @ts-ignore
          const deferredPrompt = window.beforeInstallPrompt;
          if (!deferredPrompt) {
            dispatch(mainActions.setShowInstallPrompt(false));
            return;
          }

          deferredPrompt.prompt();
          deferredPrompt.userChoice.then((choiceResult) => {
            if (choiceResult.outcome === 'accepted') {
              console.log('User accepted the A2HS prompt');
              statReachGoal('push_notify_enable');
              initPush(dispatch);
            } else {
              console.log('User dismissed the A2HS prompt');
            }

            // @ts-ignore
            window.beforeInstallPrompt = null;
            setStorageItem('skipPwaBanner', 'true');
            dispatch(mainActions.setShowInstallPrompt(false));
          });
        } else if (action === 'buy_adult_subscription') {
          dispatch(
            balancePayActions.open({
              amount: actionParams.price,
              action: BalancePayAction.SUBSCRIPTION_ADULT,
              objectId: SubscriptionType.Adult,
            }),
          );
          dispatch(feedActions.hideNotificationFromUI(notification.id));
        } else if (action === 'bind_email') {
          router.navigate(BIND_EMAIL);
          dispatch(feedActions.hideNotificationFromUI(notification.id));
        } else if (action === 'buy_sponsor_subscription') {
          dispatch(
            balancePayActions.open({
              amount: actionParams.price,
              action: BalancePayAction.SUBSCRIPTION_PERSONAL,
              objectId: actionParams.userId,
            }),
          );
          dispatch(feedActions.hideNotificationFromUI(notification.id));
        } else {
          if (notification.actionUrl === '/subscription/adult') {
            statReachGoal('show_paywall_from_feed_notify');
          }

          if (notification.id === 'online_models') {
            statReachGoal('feed_go_to_online_models');
          }

          const parsed = router.matchUrl(notification.actionUrl);
          if (parsed && !notification.actionUrl.match('https')) {
            router.navigate(parsed.name, parsed.params);
          } else {
            window.open(notification.actionUrl, '_blank');
          }
        }
      }
    },
    [notification],
  );

  return (
    <div
      className={classNames({
        FeedNotification: true,
        [style]: true,
      })}
      onClick={handleClick}
    >
      {!!notification.iconUrl && (
        <div
          className="FeedNotification__icon"
          style={{ backgroundImage: `url(${notification.iconUrl})` }}
        />
      )}
      <div className="FeedNotification__cont">
        <div className="FeedNotification__title">{notification.title}</div>
        <div className="FeedNotification__description">{notification.description}</div>
        <div className="FeedNotification__action">
          <Button size="small">{notification.actionText}</Button>
          {!skipClose && (
            <Button size="small" mode="quinary">
              {t('feed_notify_hide')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
